import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import DataGridRowHeader from './data-grid-row-header';
import { DataGridColumn, DataGridRowTree } from './models';
import DataGridCell from './data-grid-cell';
import DataGridTextCell from './data-grid-text-cell';
import DataGridDetailTable from './detail-table';
import clsx from 'clsx';
import { neutral } from '../theme/colors';
import DataGridTrendsViz from './data-grid-trends-viz';
import { DateFormatter, DateTimeFormatter } from '../shared/date-format';

export interface DataGridRowProps {
  columns: DataGridColumn[];
  columnCount: number;
  row: DataGridRowTree;
  parentCollapsed: boolean;
  accessToken?: any;
}

const useStyles = makeStyles<Theme, { parentCollapsed: boolean }>({
  cell: {
    height: ({ parentCollapsed }) => (parentCollapsed ? 0 : 50),
    transition: 'height 300ms'
  },
  rootRow: {
    borderTop: `2px solid ${neutral[400]}`,
    '& > div': {
      height: 54
    }
  }
});

const renderTextContent = (content: string, format?: string) => {
  if (format) {
    if (format == 'date') {
      return DateFormatter.format(new Date(content));
    } else if (format == 'datetime') {
      return DateTimeFormatter.format(new Date(content));
    }
  }
  return content;
};

/**
 * Render a row of the data grid, recursively rendering any children (initially
 * collapsed) and a detail table when the current level has detail data
 */
const DataGridRow = ({
  columns,
  columnCount,
  row,
  parentCollapsed,
  accessToken
}: DataGridRowProps) => {
  const classes = useStyles({ parentCollapsed });
  const [collapsed, setCollapsed] = React.useState<boolean>(true);

  React.useEffect(() => {
    parentCollapsed && setCollapsed(true);
  }, [parentCollapsed]);

  return (
    <React.Fragment>
      <DataGridCell className={clsx(classes.cell, row.nestLevel == 0 && classes.rootRow)}>
        <DataGridRowHeader
          title={row.title}
          subtitle={row.subtitle}
          statuses={row.statuses}
          nestLevel={row.nestLevel}
          collapsible={row.hasChildren}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
      </DataGridCell>
      {columns
        .filter((column) => column.key != 'header' && column.key != 'trends')
        .map((column) => (
          <DataGridCell
            className={clsx(classes.cell, row.nestLevel == 0 && classes.rootRow)}
            key={column.key}
          >
            <DataGridTextCell value={renderTextContent(row[column.key], column.format)} />
          </DataGridCell>
        ))}
      <DataGridCell className={clsx(classes.cell, row.nestLevel == 0 && classes.rootRow)}>
        <DataGridTrendsViz
          parentCollapsed={parentCollapsed}
          trends={row.trends}
          accessToken={accessToken}
        />
      </DataGridCell>
      {row.detailTable && row.detailTable.rows.length > 0 && (
        <DataGridDetailTable
          detailTable={row.detailTable}
          columnCount={columnCount}
          collapsed={collapsed}
          nestLevel={row.nestLevel}
        />
      )}
      {row.hasChildren &&
        row.children.length > 0 &&
        row.children.map((subrow, i) => (
          <DataGridRow
            columns={columns}
            columnCount={columnCount}
            row={subrow}
            parentCollapsed={collapsed}
            accessToken={accessToken}
            key={`${subrow.title}-${i}`}
          />
        ))}
    </React.Fragment>
  );
};

export default DataGridRow;
